import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AdvancedReportConfig } from '@app/advanced-reports/models/advanced-report-config';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EMPTY, Observable, of } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';
import { getIntQueryParam } from '@utils/route-utils';
import { pensionContributionsGridColumns } from './pension-contributions-grid-columns';
import { PensionContributionsDataProvider } from './state/pension-contributions.data-provider';
import { PensionContributionsResponse } from './state/pension-contribution-employee.model';

@UntilDestroy()
@Component({
  templateUrl: './pension-contributions-advanced-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PensionContributionsDataProvider],
  styleUrls: ['./pension-contributions-advanced-view.component.scss'],
  standalone: false
})
export class PensionContributionsAdvancedViewComponent {
  protected readonly config$: Observable<AdvancedReportConfig>;
  protected readonly hasErrors$: Observable<boolean>;
  protected readonly rowData$: Observable<any[]>;

  private readonly submissionId: number | null;
  private readonly response$: Observable<PensionContributionsResponse>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly dataProvider: PensionContributionsDataProvider
  ) {
    this.submissionId = getIntQueryParam(this.route, 'submissionId');
    this.response$ = this.initializeResponse();
    this.hasErrors$ = this.initializeHasErrors();
    this.rowData$ = this.initializeRowData();
    this.config$ = this.initializeConfig();
  }

  private initializeResponse(): Observable<PensionContributionsResponse> {
    if (this.submissionId === null) {
      return EMPTY;
    }

    return this.dataProvider.getSubmissionContributions$(this.submissionId).pipe(take(1), untilDestroyed(this), shareReplay(1));
  }

  private initializeHasErrors(): Observable<boolean> {
    return this.response$.pipe(map((response) => response.contributions.some((c) => c.errorDescription)));
  }

  private initializeRowData(): Observable<any[]> {
    return this.response$.pipe(
      map((response) => {
        return response.contributions.slice().sort((a, b) => {
          if (a.errorDescription && !b.errorDescription) return -1;
          if (!a.errorDescription && b.errorDescription) return 1;
          return a.employeeName.localeCompare(b.employeeName);
        });
      })
    );
  }

  private initializeConfig(): Observable<AdvancedReportConfig> {
    return this.response$.pipe(
      map((response) => ({
        gridId: 'pension-contributions',
        reportTitle: `Pensions connect / ${response.payrollName}`,
        reportType: 'standard',
        gridColumns$: of(pensionContributionsGridColumns())
      }))
    );
  }
}
